import { useAgentCode } from '@/utils/agent';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useGetVerifyAgentQuery } from '../apis';
import { APP_ID, BRANCH_CODE } from '../constants';
import { Product } from '../enums';
import { AgentStatus } from '../enums/agentStatus';
import { useAppDispatch, useAppSelector } from '../reducers';
import { updateAgent, updateAgentVerification } from '../reducers/application';
import { agentSchema } from '../schemas/agent';
import { AgentFormValues } from '../types/form/agent';
import { useRoute } from './useRoute';

export const useAgent = () => {
  const application = useAppSelector((state) => state.application);
  const formMethods = useForm<AgentFormValues>({
    resolver: yupResolver(agentSchema),
    defaultValues: application.agent,
  });
  const { redirect, product } = useRoute();
  const dispatch = useAppDispatch();
  const { data } = useGetVerifyAgentQuery(
    {
      appId: APP_ID,
      agentCode: application.agent?.agentCode ?? '',
    },
    { skip: product !== Product.EasyHealth },
  );
  const agentCode = useAgentCode();

  useEffect(() => {
    // use default agent if not GAB
    if (product !== Product.EasyHealth) {
      if (application.agent?.agentCode && application.agent?.agentBranch) {
        return;
      }

      dispatch(
        updateAgent({
          agent: {
            agentCode: agentCode,
            agentBranch: BRANCH_CODE,
          },
        }),
      );
    }
  }, [
    agentCode,
    application.agent?.agentBranch,
    application.agent?.agentCode,
    dispatch,
    product,
  ]);

  const onVerify = (formValues: AgentFormValues) => {
    try {
      const result = data?.data;
      dispatch(
        updateAgent({
          agent: {
            agentCode: formValues.agentCode,
            agentName: result?.agentName,
            agentBranch: BRANCH_CODE,
          },
          isValidAgent: result?.status === AgentStatus.Active,
        }),
      );
      if (result?.status !== AgentStatus.Active) {
        formMethods.setError('agentCode', {
          type: 'manual',
          message: 'invalid.agentCode',
        });
      }
    } catch {
      redirect('error');
    }
  };

  const onTryAgain = async () => {
    // reset agent flow
    dispatch(
      updateAgent({
        agent: undefined,
        isValidAgent: false,
      }),
    );
    formMethods.reset();
  };

  const onProceed = () => {
    if (application.isValidAgent) {
      dispatch(updateAgentVerification(true));
    }
  };

  return {
    formMethods,
    onVerify,
    application,
    onProceed,
    onTryAgain,
  };
};
